/**
 * P&L Report (Income Statement)
 *
 * Google Sheets-like report showing P&L data with:
 * - Rows = Line items (Revenue, COGS, Expenses, Net Income)
 * - Columns = Time periods (dynamically generated: years, months, or days)
 * - Conditional formatting (negative values in red, subtotals bold)
 * - Export to Excel
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Refresh,
  FileDownload,
  ReceiptLong,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useBrand } from '../../context/BrandContext';
import financialConfigService from '../../services/financialConfigService';
import { getDateRangeForPreset, getDefaultPreset, toLocalISO } from './plDatePresets';
import { DrillDownDrawer } from './drilldown';
import * as XLSX from 'xlsx';

// Live drill-down: which row ids are clickable?
// - Marketing channels (marketing_*) and OpEx categories (opex_*) — leaf data
// - Phase 2 leaf data rows below (gross_revenue / refunds / cogs / payment_fees / shipping_costs / handling_fees)
// - Calculated rows — formula breakdown
const LIVE_DRILLABLE_IDS = new Set([
  // Calculated rows (formula view)
  'net_revenue', 'gross_profit', 'gross_margin_pct',
  'total_fulfillment', 'cm1', 'cm1_margin_pct',
  'total_marketing', 'cm2', 'cm2_margin_pct',
  'opex_total', 'ebitda', 'ebitda_margin_pct',
  'ebit', 'net_income_from_gl',
  // Phase 2 leaf data rows
  'gross_revenue', 'refunds', 'cogs',
  'payment_fees', 'shipping_costs', 'handling_fees',
  // Phase 3 — cash + pending queue
  'ending_cash_balance', 'pending_queue_estimate',
  // Phase 4 — VAT/GST drill (brand → country → date → order)
  'taxes_collected',
]);

function isRowDrillable(rowOriginal) {
  if (!rowOriginal) return false;
  const t = rowOriginal.rowType;
  if (t === 'header' || t === 'spacer') return false;
  const id = rowOriginal.id || '';
  // Skip the "Not yet booked" placeholders (D&A, Interest, Tax) until they have GL entries.
  if (rowOriginal.label && rowOriginal.label.includes('Not yet booked')) return false;
  if (id.startsWith('marketing_')) return true;
  if (id.startsWith('opex_') && id !== 'opex_total') return true;
  if (id.startsWith('cogs_gl_recon_')) return true;
  if (id.startsWith('above_cm2_gl_recon_')) return true;
  return LIVE_DRILLABLE_IDS.has(id);
}

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.0%';
  return `${value.toFixed(1)}%`;
};

// Helper to parse URL params into initial filter state
const parseUrlParams = (searchParams) => {
  const urlBrand = searchParams.get('brand');
  const urlPeriod = searchParams.get('period');
  const urlPreset = searchParams.get('preset');
  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');
  const urlGeo = searchParams.get('geo');
  const urlChannel = searchParams.get('channel');

  // Determine date preset and range
  let datePreset = urlPreset || getDefaultPreset();
  let dateRange;

  if (urlStart && urlEnd) {
    // Custom dates from URL
    dateRange = { start: urlStart, end: urlEnd };
    datePreset = 'custom';
  } else if (urlPreset && urlPreset !== 'custom') {
    dateRange = getDateRangeForPreset(urlPreset);
  } else {
    datePreset = getDefaultPreset();
    dateRange = getDateRangeForPreset(datePreset);
  }

  return {
    selectedView: urlBrand || 'all',
    periodView: urlPeriod || 'monthly',
    datePreset,
    dateRange,
    selectedGeography: urlGeo || 'ALL',
    selectedChannel: urlChannel || 'ALL',
  };
};

// basis: 'accrual' = the P&L page (/finance/pl, sole P&L since 2026-07-10) —
// below-CM2 OpEx re-dated to invoice dates for invoice-covered vendors
// (backend ACCRUAL_VENDOR_SUBSTITUTIONS); drill-down passes the same basis
// flag so OpEx detail matches the re-dated cells. basis=null keeps the old
// payment-dated (cash) behavior; no route renders it anymore.
export default function PLReport({ basis = null }) {
  const { selectedBrandId, brands } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plData, setPlData] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  // Stage 1.4: GL integrity banner — surfaces known issues that affect P&L accuracy
  const [glIntegrity, setGlIntegrity] = useState(null);

  // Initialize state from URL params (only computed once via useState initializer)
  const [selectedView, setSelectedView] = useState(() => parseUrlParams(searchParams).selectedView);
  const [periodView, setPeriodView] = useState(() => parseUrlParams(searchParams).periodView);
  const [datePreset, setDatePreset] = useState(() => parseUrlParams(searchParams).datePreset);
  const [dateRange, setDateRange] = useState(() => parseUrlParams(searchParams).dateRange);
  const [selectedGeography, setSelectedGeography] = useState(() => parseUrlParams(searchParams).selectedGeography);
  const [selectedChannel, setSelectedChannel] = useState(() => parseUrlParams(searchParams).selectedChannel);

  // Drill-down drawer state. Hydrated from URL params on first load.
  const [drillTarget, setDrillTarget] = useState(() => {
    const r = searchParams.get('drill_row');
    if (!r) return null;
    const p = searchParams.get('drill_period');
    let path = [];
    const encoded = searchParams.get('drill_path');
    if (encoded) {
      try { path = JSON.parse(decodeURIComponent(escape(atob(encoded)))); } catch (e) { path = []; }
    }
    return { row_id: r, period: p === 'total' || !p ? null : p, drill_path: path };
  });

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Single source of truth for the filter → URL params mapping. Both URL-sync
  // effects build from this: copying the router's searchParams snapshot instead
  // let one effect re-add a param the other had just stripped (stale snapshot).
  const buildFilterParams = () => {
    const params = new URLSearchParams();

    // Set params only where they differ from defaults
    if (selectedView && selectedView !== 'all') {
      params.set('brand', selectedView);
    }

    if (periodView && periodView !== 'monthly') {
      params.set('period', periodView);
    }

    // For date params: if custom, store start/end; otherwise store preset
    if (datePreset === 'custom') {
      params.set('start', dateRange.start);
      params.set('end', dateRange.end);
    } else if (datePreset && datePreset !== getDefaultPreset()) {
      params.set('preset', datePreset);
    }

    if (selectedGeography && selectedGeography !== 'ALL') {
      params.set('geo', selectedGeography);
    }

    if (selectedChannel && selectedChannel !== 'ALL') {
      params.set('channel', selectedChannel);
    }

    return params;
  };

  // SINGLE writer for the URL: filter params always, plus drill params
  // (drill_row/drill_period/drill_path) whenever a drill is open. This is the
  // ONLY effect that calls setSearchParams, on purpose. When two separate
  // effects both wrote the URL, one would re-fire and clobber the params the
  // other had just written — that is how drill deep-links used to get wiped a
  // render later (react-router re-mints setSearchParams on every URL change, so
  // any effect that depends on it, or indirectly re-runs on a URL change, could
  // overwrite the drill params with filter-only params). Collapsing to one
  // writer removes that whole class. setSearchParams is intentionally NOT a
  // dependency (it changes identity on every URL write); depend on the actual
  // filter/drill state instead. Do NOT switch the call to the functional
  // setSearchParams(prev => ...) form — that would read the (omitted) live
  // searchParams and reintroduce a stale-closure hazard.
  useEffect(() => {
    if (!isInitialized) return;
    const params = buildFilterParams();
    if (drillTarget) {
      params.set('drill_row', drillTarget.row_id);
      params.set('drill_period', drillTarget.period || 'total');
      if (drillTarget.drill_path && drillTarget.drill_path.length > 0) {
        // base64 of the JSON path. unescape/encodeURIComponent dance to handle non-ASCII labels.
        const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(drillTarget.drill_path))));
        params.set('drill_path', encoded);
      }
    }
    // Use replace (not push) — single URL slot reflects current filter+drill state.
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, periodView, datePreset, dateRange, selectedGeography, selectedChannel, drillTarget, isInitialized]);

  // When page filters change, close the drawer — drill totals would be stale otherwise.
  useEffect(() => {
    if (!isInitialized) return;
    if (drillTarget !== null) setDrillTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, dateRange, selectedGeography, selectedChannel, periodView]);

  // Only sync from brand context if no brand is specified in URL
  useEffect(() => {
    if (!searchParams.get('brand') && selectedBrandId) {
      setSelectedView(selectedBrandId.toString());
    }
  }, [selectedBrandId, searchParams]);

  useEffect(() => {
    if (selectedView) {
      loadPLData();
    }
  }, [selectedView, dateRange, periodView, selectedGeography, selectedChannel, basis]);

  // Stage 1.4: fetch GL integrity once per period change.
  // Hits the same /api/finance/transactions/summary endpoint that powers the
  // transactions browser; surfaces the duplicate-JE count (which inflates OpEx).
  useEffect(() => {
    let cancelled = false;
    const fetchIntegrity = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const url = `${apiUrl}/api/finance/transactions/summary?start_date=${encodeURIComponent(dateRange.start)}&end_date=${encodeURIComponent(dateRange.end)}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json.success) setGlIntegrity(json.data);
      } catch (e) {
        // Non-fatal — banner just won't show
      }
    };
    fetchIntegrity();
    return () => { cancelled = true; };
  }, [dateRange.start, dateRange.end]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === 'custom') {
      return; // Keep existing date range for custom
    }
    setDateRange(getDateRangeForPreset(preset));
  };

  const loadPLData = async () => {
    try {
      setLoading(true);
      setError(null);

      const groupByMap = {
        'annual': 'year',
        'monthly': 'month',
        'daily': 'day'
      };

      const params = {
        brand_id: selectedView === 'all' ? null : parseInt(selectedView),
        start_date: dateRange.start,
        end_date: dateRange.end,
        group_by: groupByMap[periodView],
        market: selectedGeography === 'ALL' ? null : selectedGeography,
        channel: selectedChannel === 'ALL' ? null : selectedChannel,
        ...(basis === 'accrual' ? { basis: 'accrual' } : {}),
      };

      console.log('[P&L Report] Generating Income Statement with params:', params);

      const response = await financialConfigService.generateIncomeStatement(params);

      if (response.success) {
        console.log('[P&L Report] Received P&L data:', response.data);
        setPlData(response.data);
      } else {
        setError(response.error || 'Failed to generate P&L report');
      }
    } catch (err) {
      console.error('[P&L Report] Error loading P&L data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  };

  // Transform data for table display
  const tableData = useMemo(() => {
    if (!plData || !plData.details) return [];

    // Helper to extract period value from backend data
    const getBackendValue = (dataKey, periodKey) => {
      const detailsForKey = plData.details[dataKey];
      if (!detailsForKey || !Array.isArray(detailsForKey)) return 0;

      const periodData = detailsForKey.find(item => {
        const key = item.period || item.group_key;
        return key === periodKey;
      });

      if (!periodData) return 0;

      // Map dataKeys to actual backend column names
      const columnMap = {
        'revenue': 'total_revenue_usd',
        'refunds': 'total_refunds_usd',
        'taxes_collected': 'total_taxes_usd',
        'cogs': 'total_cogs_usd',
        'inventoryAdjustments': 'total_inventory_adjustment_usd',
        'paymentFees': 'total_fees_usd',
        'shippingCosts': 'total_shipping_usd',
        'handlingFees': 'total_handling_usd',
        'importDuties': 'total_import_duty_usd',
        'newCustomerRevenue': 'new_customer_revenue_usd',
        'repeatCustomerRevenue': 'repeat_revenue_usd',
        'unknownCustomerRevenue': 'unknown_customer_revenue_usd',
        'marketingExpenses': 'total_spend_usd',
        'qbExpenses': 'total_expenses_usd'
      };

      const columnName = columnMap[dataKey] || dataKey;
      return periodData[columnName] || periodData.total || 0;
    };

    // Get all unique periods
    const periodsSet = new Set();
    Object.values(plData.details).forEach(detailArray => {
      if (Array.isArray(detailArray)) {
        detailArray.forEach(item => {
          const periodKey = item.period || item.group_key;
          if (periodKey) periodsSet.add(periodKey);
        });
      }
    });
    // #29: era-aware marketing can have periods with GL-only spend that no
    // platform detail array carries (e.g. 2020 — GL ads, no platform data).
    (plData.summary?.marketing_effective?.details || []).forEach(d => {
      if (d.period) periodsSet.add(d.period);
    });
    const allPeriods = Array.from(periodsSet).sort();

    // Helper to create a data row
    const createDataRow = (id, label, dataKey, rowType = 'data', isPercentage = false) => {
      const row = {
        id,
        label,
        rowType,
        isPercentage,
        total: plData.summary?.[id] ?? 0
      };

      // Add period-specific values from backend
      allPeriods.forEach(periodKey => {
        row[periodKey] = getBackendValue(dataKey, periodKey);
      });

      return row;
    };

    // Helper to create a calculated row
    const createCalculatedRow = (id, label, calculateFn, rowType = 'subtotal', isPercentage = false) => {
      const row = {
        id,
        label,
        rowType,
        isPercentage,
        total: plData.summary?.[id] ?? 0
      };

      // Calculate value for each period
      allPeriods.forEach(periodKey => {
        row[periodKey] = calculateFn(periodKey);
      });

      return row;
    };

    const rows = [];

    // REVENUE SECTION
    rows.push({ id: 'header_revenue', label: 'SALES / REVENUE', rowType: 'header' });

    // DT-1995: a failed query behind a row is said on that row, the same way as inventory_adjustments_error below.
    const unavailable = (label, ...errs) => {
      const e = [...new Set(errs.filter(Boolean))];
      return e.length ? `${label} (UNAVAILABLE — excluded: ${e.join('; ')})` : label;
    };
    // retail_orders_error joins the failed wholesale legs' fixed texts (service.js retailErrorOf): each part goes
    // to its own row; a part no row claims goes on Gross Revenue, so it is never dropped.
    const retailParts = String(plData.summary?.retail_orders_error || '').split('; ').filter(Boolean);
    const RETAIL_ROWS = { cogs: /COGS/, shipping: /shipping/i, handling: /handling/i };
    const retailFor = (row) => retailParts.filter(t => (row === 'revenue'
      ? !Object.values(RETAIL_ROWS).some(re => re.test(t))
      : RETAIL_ROWS[row].test(t)));

    const grossRevenue = createDataRow('gross_revenue', unavailable('Gross Revenue', ...retailFor('revenue')), 'revenue');
    rows.push(grossRevenue);

    // Retail (wholesale) has no refunds stream, gateway fees, or ad spend — those
    // rows are structurally zero for that channel and are hidden, not shown as $0.
    const isRetailView = selectedChannel === 'Retail';

    const refunds = createDataRow('refunds', 'Refunds', 'refunds');
    // Backend summary carries refunds/taxes SIGNED (negative) while the period
    // detail cells are positive magnitudes — abs() the Total so the row reads
    // consistently across columns (the "Less:"/row semantics carry the sign).
    refunds.total = Math.abs(refunds.total);
    if (!isRetailView) rows.push(refunds);

    // Stage 1.2a (2026-05-04): VAT/GST collected — sum of orders_table.taxes (Shopify direct).
    // Net Revenue is restated NET of VAT since VAT was never our revenue (it's a liability
    // owed to tax authorities). All margin %s downstream restate on this smaller base.
    const taxesCollected = createDataRow('taxes_collected', 'Less: VAT/GST collected', 'taxes_collected');
    taxesCollected.total = Math.abs(taxesCollected.total);
    rows.push(taxesCollected);

    const netRevenue = createCalculatedRow(
      'net_revenue',
      'Net Revenue',
      (period) => (grossRevenue[period] || 0) - Math.abs(refunds[period] || 0) - Math.abs(taxesCollected[period] || 0),
      'subtotal'
    );
    rows.push(netRevenue);

    // COGS SECTION
    rows.push({ id: 'header_cogs', label: 'COST OF GOODS SOLD', rowType: 'header' });

    // S1.2b: COGS coverage % — what fraction of orders have a non-zero cost_of_goods.
    // Low coverage means Gross Profit is overstated (orders without product-master COGS
    // contribute $0 to COGS but full revenue). Surface as a label suffix only when there
    // are actual orders to report on (avoids "100% of 0 orders" cosmetic).
    const cogsCoverage = plData.summary?.cogs_coverage_pct;
    const cogsOrderCount = plData.summary?.cogs_order_count || 0;
    const cogsLabel = (typeof cogsCoverage === 'number' && cogsOrderCount > 0)
      ? `COGS  (coverage: ${cogsCoverage.toFixed(1)}% of ${cogsOrderCount.toLocaleString()} orders)`
      : 'COGS';
    const cogs = createDataRow('cogs', unavailable(cogsLabel, ...retailFor('cogs')), 'cogs');
    rows.push(cogs);

    // S1.2c v3 (2026-05-04): GL 7100/7200 info-only rows — one per account.
    // Surfaces CIN7 vendor wires mis-booked as immediate expense (Dr 7100/7200
    // / Cr Bank) instead of BS Inventory / AP. Above-CM2 COGS already comes
    // from orders_table.cost_of_goods (accrual-correct, recognized at sale).
    // These rows are INFO-ONLY — italic-styled, never subtracted from any
    // calculation. Drops to 0 once Stage 2.3 CIN7 matchers re-classify the
    // wires from P&L 7100/7200 to BS 1200/2100.
    const cogsGl = plData.summary?.cogs_gl_reconciliation;
    if (cogsGl && cogsGl.total_usd && Math.abs(cogsGl.total_usd) > 1) {
      (cogsGl.breakdown || []).forEach(b => {
        const reconRow = {
          id: `cogs_gl_recon_${b.account_code}`,
          label: `  ℹ ${b.account_code} ${b.account_name}`,
          rowType: 'info',
          isPercentage: false,
          total: b.net_usd
        };
        allPeriods.forEach(periodKey => {
          const cell = (cogsGl.details_per_account || []).find(d =>
            (d.period || d.group_key) === periodKey && d.account_code === b.account_code);
          reconRow[periodKey] = cell ? (parseFloat(cell.net_usd_per_period) || 0) : 0;
        });
        rows.push(reconRow);
      });
    }

    // 2026-10-02: GL 7300 Inventory Adjustment / Write-off — the monthly valuation true-up
    // (shrinkage, count corrections, write-offs). A cost of sales, so it reduces Gross Profit.
    // Same pattern as Import Duties: rendered only when nonzero.
    // Shown when any period is nonzero (offsetting months would net the total to 0), and always
    // when the backend could not value 7300 — Gross Profit then excludes it and must say so.
    const invAdjError = plData.summary?.inventory_adjustments_error;
    const inventoryAdjustments = createDataRow('inventory_adjustments',
      invAdjError ? `Inventory Adjustments / Write-offs (UNAVAILABLE — excluded: ${invAdjError})` : 'Inventory Adjustments / Write-offs',
      'inventoryAdjustments');
    if (invAdjError || allPeriods.some(p => Math.abs(inventoryAdjustments[p] || 0) >= 0.005)
        || Math.abs(inventoryAdjustments.total || 0) >= 0.005) rows.push(inventoryAdjustments);

    const grossProfit = createCalculatedRow(
      'gross_profit',
      'Gross Profit',
      (period) => (netRevenue[period] || 0) - (cogs[period] || 0) - (inventoryAdjustments[period] || 0),
      'subtotal'
    );
    rows.push(grossProfit);

    const grossMarginPct = createCalculatedRow(
      'gross_margin_pct',
      'Gross Margin %',
      (period) => {
        const net = netRevenue[period] || 0;
        const profit = grossProfit[period] || 0;
        return net > 0 ? (profit / net) * 100 : 0;
      },
      'percent',
      true
    );
    rows.push(grossMarginPct);

    // OPERATIONAL COSTS
    rows.push({ id: 'header_ops', label: 'OPERATIONAL COSTS', rowType: 'header' });

    // 2026-05-12: italic info rows beneath Payment Fees / Shipping / Handling
    // showing what's booked to the matching 81x0 / 83x0 GL accounts. Info-only —
    // never subtracted from CM1/CM2. Mirrors the COGS GL recon pattern (line 476-493).
    const aboveCm2Gl = plData.summary?.above_cm2_gl_reconciliation;
    const pushAboveCm2GlInfoRows = (section) => {
      if (!aboveCm2Gl || !Array.isArray(aboveCm2Gl.breakdown)) return;
      aboveCm2Gl.breakdown
        .filter(b => b.section === section && Math.abs(b.net_usd || 0) > 1)
        .forEach(b => {
          const reconRow = {
            id: `above_cm2_gl_recon_${b.account_code}`,
            label: `  ℹ ${b.account_code} ${b.account_name}`,
            rowType: 'info',
            isPercentage: false,
            total: b.net_usd
          };
          allPeriods.forEach(periodKey => {
            const cell = (aboveCm2Gl.details_per_account || []).find(d =>
              (d.period || d.group_key) === periodKey && d.account_code === b.account_code);
            reconRow[periodKey] = cell ? (parseFloat(cell.net_usd_per_period) || 0) : 0;
          });
          rows.push(reconRow);
        });
    };

    // DT-1985 sweep: Amazon settlement fees fold into Payment Fees (8140), Handling (8300) and Total Marketing
    // (8000); when their query failed those rows lack them. Fixed text: the stored .error is the driver message.
    const settleFeesError = plData.summary?.amazon_settlement_fees?.error ? 'Amazon settlement fees could not be loaded' : null;
    const paymentFees = createDataRow('payment_fees', unavailable('Payment Fees', settleFeesError), 'paymentFees');
    if (!isRetailView) {
      rows.push(paymentFees);
      // 2026-08-06: sheet-parity % of revenue rows (Payment Fees / Fulfillment /
      // Total Marketing) — same Net Revenue base as the existing margin % rows.
      rows.push(createCalculatedRow(
        'payment_fees_pct',
        'Payment Fees %',
        (period) => {
          const net = netRevenue[period] || 0;
          return net > 0 ? ((paymentFees[period] || 0) / net) * 100 : 0;
        },
        'percent',
        true
      ));
      pushAboveCm2GlInfoRows('payment_fees');
    }

    const shippingCosts = createDataRow('shipping_costs', unavailable('Shipping Costs', ...retailFor('shipping')), 'shippingCosts');
    rows.push(shippingCosts);

    const handlingFees = createDataRow('handling_fees', unavailable('Handling / Warehousing', settleFeesError, ...retailFor('handling')), 'handlingFees');
    rows.push(handlingFees);

    // 2026-07-14: the backend's total_fulfillment (and CM1) includes import
    // duties — the page silently dropped them, so its CM1 disagreed with
    // summary.cm1 (and with the monthly Excel export) whenever duties ≠ 0.
    // Structurally zero for most views, so the row only renders when nonzero.
    const importDuties = createDataRow('import_duties', 'Import Duties', 'importDuties');
    if (Math.abs(importDuties.total || 0) >= 0.005) rows.push(importDuties);

    const totalFulfillment = createCalculatedRow(
      'total_fulfillment',
      'Total Fulfillment',
      (period) => (shippingCosts[period] || 0) + (handlingFees[period] || 0) + (importDuties[period] || 0),
      'subtotal'
    );
    rows.push(totalFulfillment);
    rows.push(createCalculatedRow(
      'fulfillment_pct',
      'Fulfillment %',
      (period) => {
        const net = netRevenue[period] || 0;
        return net > 0 ? ((totalFulfillment[period] || 0) / net) * 100 : 0;
      },
      'percent',
      true
    ));
    // 2026-05-12: 8300/8310/8320 italic rows sit under Total Fulfillment, not
    // under Shipping/Handling individually — the GL doesn't split fulfillment
    // into shipping vs handling, so showing them only under Shipping would
    // mislead. Total Fulfillment is the matching aggregation level.
    pushAboveCm2GlInfoRows('total_fulfillment');

    // CM1
    const cm1 = createCalculatedRow(
      'cm1',
      'CM1 (Contribution Margin 1)',
      (period) => (grossProfit[period] || 0) - (paymentFees[period] || 0) - (totalFulfillment[period] || 0),
      'total'
    );
    rows.push(cm1);

    const cm1MarginPct = createCalculatedRow(
      'cm1_margin_pct',
      'CM1 Margin %',
      (period) => {
        const net = netRevenue[period] || 0;
        const margin = cm1[period] || 0;
        return net > 0 ? (margin / net) * 100 : 0;
      },
      'percent',
      true
    );
    rows.push(cm1MarginPct);

    // 2026-07-12: helper for the terminal note in filtered views.
    const makeEndNote = (label) => {
      const note = { id: 'filtered_view_end_note', label, rowType: 'info', isPercentage: false, total: 0 };
      allPeriods.forEach(p => { note[p] = 0; });
      return note;
    };

    // Retail (wholesale) has no ad spend — the channel's P&L ends at CM1.
    if (isRetailView) {
      rows.push(makeEndNote('  ℹ Wholesale view ends at CM1 (no refunds/fees/marketing; OpEx is company-level — see unfiltered view)'));
      return rows;
    }

    // MARKETING
    rows.push({ id: 'header_marketing', label: 'MARKETING', rowType: 'header' });

    // Get unique channels from marketing breakdown
    const marketingChannels = new Set();
    const marketingDetails = plData.details.marketingExpenses;
    if (marketingDetails && Array.isArray(marketingDetails)) {
      marketingDetails.forEach(item => {
        if (item.channel) {
          marketingChannels.add(item.channel);
        }
      });
    }

    // #29 (2026-07-06): era-aware marketing — when the backend applied the
    // per-channel-month GL fallback, channel cells render from
    // marketing_effective (the numbers CM2/EBITDA actually subtract), so
    // columns foot to the Total column by construction.
    const mktEffective = plData.summary?.marketing_effective;
    const useEraAware = !!(mktEffective && mktEffective.applied && Array.isArray(mktEffective.details) && mktEffective.details.length);
    if (useEraAware) {
      mktEffective.details.forEach(d => {
        if (d.channel) marketingChannels.add(d.channel);
      });
    }

    // 2026-05-12: GL-only channels (no ads_table data) — e.g. Microsoft Ads (8070).
    // Surface them as primary channel rows sourced from the GL recon, since there
    // is no platform feed to source from. The GL value is the only source of truth
    // for these channels. (Era-aware discovery above already covers these when
    // applied; this remains for the passthrough cases.)
    // 2026-07-06 leak fix (Vishal-reported, session deda4c46): the recon is
    // computed UNFILTERED — injecting it into a market-filtered view leaks
    // company-wide GL totals (geo=US + channel=Amazon showed full fb/google GL
    // rows → monthly CM2 −131% vs Total +38.5%), and a channel-filtered view
    // must only admit that channel's GL accounts. Gate: never inject when a
    // market filter is active (GL has no market split — the ℹ passthrough note
    // row explains); gate by the channel filter with the same semantics as the
    // backend's glChannelAllowed.
    const mktGlForChannels = plData.summary?.marketing_gl_reconciliation;
    const eraReason = plData.summary?.marketing_effective?.reason;
    const glOnlyChannelAllowed = (ch) => {
      if (eraReason === 'market_filter') return false;
      if (!selectedChannel || selectedChannel === 'ALL') return true;
      if (selectedChannel === 'D2C') return ch !== 'amazon-ads';
      if (selectedChannel === 'Amazon') return ch === 'amazon-ads';
      return false; // Retail — no ad spend
    };
    if (!useEraAware && mktGlForChannels && Array.isArray(mktGlForChannels.breakdown)) {
      mktGlForChannels.breakdown.forEach(b => {
        if (b.channel && glOnlyChannelAllowed(b.channel) && !marketingChannels.has(b.channel) && Math.abs(b.net_usd || 0) > 1) {
          marketingChannels.add(b.channel);
        }
      });
    }

    // Create a row for each marketing channel
    const channelRows = {};
    Array.from(marketingChannels).sort().forEach(channel => {
      const hasAdsTableData = (marketingDetails || []).some(item => item.channel === channel);
      const channelRow = {
        id: `marketing_${channel.toLowerCase().replace(/\s+/g, '_')}`,
        // 2026-05-12: GL-only channels (e.g. microsoft-ads) get a "(GL)" suffix
        // since there's no platform feed — the GL is the source of truth, not a
        // reconciliation. No italic info row below (would just duplicate the value).
        label: hasAdsTableData ? `  ${channel}` : `  ${channel} (GL)`,
        rowType: 'data',
        isPercentage: false,
        total: 0
      };

      if (useEraAware) {
        // #29: cells + total from the era-aware effective details (platform
        // where present, GL 80x0 for pre-platform months) — the same values
        // the backend subtracts in CM2/EBITDA.
        allPeriods.forEach(periodKey => {
          const cell = mktEffective.details.find(d =>
            (d.period || d.group_key) === periodKey && d.channel === channel);
          channelRow[periodKey] = cell ? (cell.total_spend_usd || 0) : 0;
        });
        const effEntry = (mktEffective.breakdown || []).find(b => b.channel === channel);
        channelRow.total = effEntry ? (effEntry.spend || 0) : 0;
        // '*' marks channels whose range blends platform + GL era-fallback
        // months (legend = the ℹ era-fallback row below). Pure-GL channels
        // already carry the '(GL)' suffix.
        if (hasAdsTableData && effEntry && Math.abs(effEntry.fallback_usd || 0) > 0.5) {
          channelRow.label = `  ${channel} *`;
        }
      } else if (hasAdsTableData) {
        // Source from ads_table (existing path)
        allPeriods.forEach(periodKey => {
          const periodData = marketingDetails.find(item => {
            const key = item.period || item.group_key;
            return key === periodKey && item.channel === channel;
          });
          channelRow[periodKey] = periodData ? (periodData.total_spend_usd || 0) : 0;
        });
        if (plData.summary && plData.summary.marketing_breakdown) {
          const channelBreakdown = plData.summary.marketing_breakdown.find(b => b.channel === channel);
          if (channelBreakdown) {
            channelRow.total = channelBreakdown.spend || 0;
          }
        }
      } else {
        // GL-only channel — source values directly from marketing_gl_reconciliation.
        const mktGlSrc = plData.summary?.marketing_gl_reconciliation;
        const glEntry = (mktGlSrc?.breakdown || []).find(b => b.channel === channel);
        channelRow.total = glEntry ? (glEntry.net_usd || 0) : 0;
        allPeriods.forEach(periodKey => {
          const cell = (mktGlSrc?.details_per_account || []).find(d =>
            (d.period || d.group_key) === periodKey && d.channel === channel);
          channelRow[periodKey] = cell ? (parseFloat(cell.net_usd_per_period) || 0) : 0;
        });
      }

      channelRows[channel] = channelRow;
      rows.push(channelRow);

      // 2026-05-08: italic info row directly beneath each ad channel — what's
      // actually booked to the matching 80x0 GL account vs what the platform
      // reports in ads_table. Mirrors the COGS GL recon pattern (line 476-493).
      // Info-only — never subtracted from Total Marketing or CM2. Skip for
      // GL-only channels (the parent row is already the GL value).
      if (hasAdsTableData) {
        const mktGl = plData.summary?.marketing_gl_reconciliation;
        if (mktGl && Array.isArray(mktGl.breakdown)) {
          const reconEntry = mktGl.breakdown.find(b => b.channel === channel);
          if (reconEntry && Math.abs(reconEntry.net_usd || 0) > 1) {
            const reconRow = {
              id: `marketing_gl_recon_${reconEntry.account_code}`,
              label: `  ℹ ${reconEntry.account_code} ${reconEntry.account_name}`,
              rowType: 'info',
              isPercentage: false,
              total: reconEntry.net_usd
            };
            allPeriods.forEach(periodKey => {
              const cell = (mktGl.details_per_account || []).find(d =>
                (d.period || d.group_key) === periodKey && d.account_code === reconEntry.account_code);
              reconRow[periodKey] = cell ? (parseFloat(cell.net_usd_per_period) || 0) : 0;
            });
            rows.push(reconRow);
          }
        }
      }
    });

    // #29: transparency row — how much of the marketing above came from the GL
    // era-fallback (channel-months with no platform data). Info-only; the
    // channel rows above already include these amounts ('*' marks them).
    if (useEraAware && Math.abs(mktEffective.fallback_total || 0) > 0.5) {
      const eraRow = {
        id: 'marketing_era_fallback_note',
        label: '  ℹ * of which from GL 80x0 (months without platform data, or era-window top-ups to the GL yearly total)',
        rowType: 'info',
        isPercentage: false,
        total: mktEffective.fallback_total
      };
      allPeriods.forEach(periodKey => {
        eraRow[periodKey] = mktEffective.details
          .filter(d => (d.period || d.group_key) === periodKey)
          .reduce((s, d) => s + (d.fallback_usd || 0), 0);
      });
      rows.push(eraRow);
    }

    // #29: when era-aware is NOT applied (market-filtered or daily view), say so —
    // otherwise toggling a filter silently reverts historical marketing to
    // platform-only numbers and EBITDA jumps with no visible cause.
    if (mktEffective && mktEffective.applied === false &&
        (mktEffective.reason === 'market_filter' || mktEffective.reason === 'day_grouping')) {
      const noteRow = {
        id: 'marketing_era_passthrough_note',
        label: mktEffective.reason === 'market_filter'
          ? '  ℹ GL era-fallback not applied in market-filtered views (GL has no market split) — pre-2025 marketing may be understated'
          : '  ℹ GL era-fallback not applied in daily view — pre-2025 marketing may be understated',
        rowType: 'info',
        isPercentage: false,
        total: 0
      };
      allPeriods.forEach(p => { noteRow[p] = 0; });
      rows.push(noteRow);
    }

    // Total Marketing row (sum of all channels)
    // DT-1985: a failed Amazon Ads or era-window marketing query leaves spend out of the total;
    // say so on the row, the same way as inventory_adjustments_error above.
    // DT-1985 sweep: a main-path marketing GL recon failure blocks the era fallback with no .error, and a failed
    // Amazon settlement fee query drops its coupon/deal (8000) fees from the total.
    const marketingErrors = [...new Set([plData.summary?.amazon_ads_error, mktEffective?.error,
      mktEffective?.reason === 'gl_recon_error' ? 'Marketing could not be loaded for the era fallback' : null,
      settleFeesError].filter(Boolean))];
    const totalMarketing = createCalculatedRow(
      'total_marketing',
      marketingErrors.length ? `Total Marketing (UNAVAILABLE — excluded: ${marketingErrors.join('; ')})` : 'Total Marketing',
      (period) => {
        return Object.values(channelRows).reduce((sum, channelRow) => {
          return sum + (channelRow[period] || 0);
        }, 0);
      },
      'subtotal'
    );
    rows.push(totalMarketing);

    rows.push(createCalculatedRow(
      'total_marketing_pct',
      'Total Marketing %',
      (period) => {
        const net = netRevenue[period] || 0;
        return net > 0 ? ((totalMarketing[period] || 0) / net) * 100 : 0;
      },
      'percent',
      true
    ));

    // CM2
    const cm2 = createCalculatedRow(
      'cm2',
      'CM2 (Contribution Margin 2)',
      (period) => (cm1[period] || 0) - (totalMarketing[period] || 0),
      'total'
    );
    rows.push(cm2);

    const cm2MarginPct = createCalculatedRow(
      'cm2_margin_pct',
      'CM2 Margin %',
      (period) => {
        const net = netRevenue[period] || 0;
        const margin = cm2[period] || 0;
        return net > 0 ? (margin / net) * 100 : 0;
      },
      'percent',
      true
    );
    rows.push(cm2MarginPct);

    // 2026-08-06: NC/RC revenue split under CM2 (sheet parity), from
    // orders_table.is_new_customer — follows the same brand/geo/channel/date
    // filters as Gross Revenue's orders_table leg.
    rows.push(createDataRow('new_customer_revenue', 'New Customer Revenue', 'newCustomerRevenue'));
    rows.push(createDataRow('repeat_customer_revenue', 'Repeat Customer Revenue', 'repeatCustomerRevenue'));
    // Orders whose customer can't be identified (Amazon sends no buyer email), so new vs repeat is unknown.
    rows.push(createDataRow('unknown_customer_revenue', 'Unknown Customer Revenue', 'unknownCustomerRevenue'));

    // 2026-07-12: below-CM2 is GL-scoped and the GL has no channel/market
    // dimension — filtered views END at CM2 (backend sends nulls + this flag;
    // previously a Retail-filtered view showed EBITDA = $17k wholesale revenue
    // minus $107k company-wide OpEx).
    if (plData.summary?.below_cm2_applicable === false) {
      rows.push(makeEndNote('  ℹ Filtered view ends at CM2 (OpEx/EBITDA are company-level — see unfiltered view)'));
      return rows;
    }

    // OPERATING EXPENSES — Internal GL (Phase 3 v1) ----------------------------
    rows.push({ id: 'header_opex', label: 'OPERATING EXPENSES (Internal GL)', rowType: 'header' });

    // Helper: sum opexExpenses details for a given category + period
    const opexDetails = plData.details?.opexExpenses || [];
    const opexCategorySumPerPeriod = (categoryId, periodKey) => {
      return opexDetails
        .filter(d => d.category_id === categoryId && (d.period === periodKey || d.period?.startsWith(periodKey)))
        .reduce((s, d) => s + (Number(d.amount) || 0), 0);
    };

    // Render each category from the breakdown (sorted by amount descending — backend already sorts)
    const opexBreakdown = plData.summary?.opex_breakdown || [];
    const opexCategoryRows = opexBreakdown.map(cat => {
      const row = {
        id: `opex_${cat.id}`,
        label: cat.label,
        rowType: 'data',
        isPercentage: false,
        total: cat.amount || 0
      };
      allPeriods.forEach(periodKey => {
        row[periodKey] = opexCategorySumPerPeriod(cat.id, periodKey);
      });
      return row;
    });
    opexCategoryRows.forEach(r => rows.push(r));

    // Total OpEx subtotal
    // DT-1985: the OpEx GL query failed, so this 0 is not a real 0 (same note as inventory_adjustments_error).
    const opexError = plData.summary?.opex_error;
    const totalOpex = createCalculatedRow(
      'opex_total',
      opexError ? `Total Operating Expenses (UNAVAILABLE — excluded: ${opexError})` : 'Total Operating Expenses',
      (period) => opexCategoryRows.reduce((s, r) => s + (r[period] || 0), 0),
      'subtotal'
    );
    rows.push(totalOpex);

    // Pending queue transparency: outgoing rows in window not yet journaled.
    // EBITDA shown is upper bound; true EBITDA is somewhere between (EBITDA) and (EBITDA - pending_queue).
    const pendingTotal = plData.summary?.pending_queue_estimate_usd || 0;
    // DT-1995: a failed estimate is not "nothing pending": the row shows with the note.
    const pendingError = plData.summary?.pending_queue_estimate_error;
    if (pendingTotal > 0 || pendingError) {
      const pendingRow = {
        id: 'pending_queue_estimate',
        label: unavailable('⚠ Pending Queue (not yet booked — see /finance/journal-reconciliation)', pendingError),
        rowType: 'data',
        isPercentage: false,
        total: pendingTotal
      };
      // Pending is a window-level estimate (no per-period breakdown in API yet) — show on total only.
      allPeriods.forEach(p => { pendingRow[p] = 0; });
      rows.push(pendingRow);
    }

    // EBITDA = CM2 - OpEx
    const ebitda = createCalculatedRow(
      'ebitda',
      'EBITDA',
      (period) => (cm2[period] || 0) - (totalOpex[period] || 0),
      'subtotal'
    );
    rows.push(ebitda);

    const ebitdaMarginPct = createCalculatedRow(
      'ebitda_margin_pct',
      'EBITDA Margin %',
      (period) => {
        const margin = ebitda[period] || 0;
        const rev = netRevenue[period] || 0;
        return rev > 0 ? (margin / rev) * 100 : 0;
      },
      'subtotal',
      true
    );
    rows.push(ebitdaMarginPct);

    // Below-the-line — placeholders until COA + ledgers ship (Phase 5)
    const missingLines = plData.summary?.missing_lines || [];
    const isMissing = (key) => missingLines.includes(key);
    // DT-1995: when the D&A / interest / tax query failed, these 0s are unknown, not "not yet booked".
    const belowEbitdaError = plData.summary?.below_ebitda_error;
    const belowLabel = (label, key) => (belowEbitdaError
      ? unavailable(label, belowEbitdaError)
      : label + (isMissing(key) ? ' (Not yet booked)' : ''));

    const depreciation = {
      id: 'depreciation_amortization',
      label: belowLabel('Depreciation & Amortization', 'depreciation_amortization'),
      rowType: 'data',
      isPercentage: false,
      total: plData.summary?.depreciation_amortization ?? 0
    };
    allPeriods.forEach(p => { depreciation[p] = 0; });
    rows.push(depreciation);

    // EBIT = EBITDA - D&A
    const ebit = createCalculatedRow(
      'ebit',
      unavailable('EBIT', belowEbitdaError),
      (period) => (ebitda[period] || 0) - (depreciation[period] || 0),
      'subtotal'
    );
    rows.push(ebit);

    const interestExpense = {
      id: 'interest_expense',
      label: belowLabel('Interest Expense', 'interest_expense'),
      rowType: 'data',
      isPercentage: false,
      total: plData.summary?.interest_expense ?? 0
    };
    allPeriods.forEach(p => { interestExpense[p] = 0; });
    rows.push(interestExpense);

    const taxExpense = {
      id: 'tax_expense',
      label: belowLabel('Corporate Tax', 'tax_expense'),
      rowType: 'data',
      isPercentage: false,
      total: plData.summary?.tax_expense ?? 0
    };
    allPeriods.forEach(p => { taxExpense[p] = 0; });
    rows.push(taxExpense);

    // Net Income (= EBIT - Interest - Tax). Revenue/COGS/fees/marketing above come from orders and
    // ad platforms and only OpEx and below from the GL, so this is NOT the ledger's net income.
    const netIncomeGL = createCalculatedRow(
      'net_income_from_gl',
      unavailable('NET INCOME (operational P&L)', belowEbitdaError),
      (period) => (ebit[period] || 0) - (interestExpense[period] || 0) - (taxExpense[period] || 0),
      'final'
    );
    rows.push(netIncomeGL);

    // Ledger net income — ties to the Balance Sheet's retained-earnings movement — and the bridge
    // from the operational figure above. Company-wide unfiltered views only (null otherwise).
    const glNi = plData.summary?.gl_net_income;
    if (glNi && glNi.bridge) {
      const ledgerNi = {
        id: 'gl_net_income_ledger',
        label: 'NET INCOME (per ledger)',
        rowType: 'total',
        isPercentage: false,
        total: glNi.net_income
      };
      allPeriods.forEach(periodKey => {
        const d = (glNi.details || []).find(x => x.period === periodKey);
        ledgerNi[periodKey] = d ? d.net_income_usd : 0;
      });
      rows.push(ledgerNi);

      const diffRow = {
        id: 'gl_net_income_difference',
        label: '  ℹ Difference (operational − ledger)',
        rowType: 'info',
        isPercentage: false,
        total: (glNi.bridge.net_income_operational || 0) - (glNi.bridge.net_income_gl || 0)
      };
      allPeriods.forEach(periodKey => {
        diffRow[periodKey] = (netIncomeGL[periodKey] || 0) - (ledgerNi[periodKey] || 0);
      });
      rows.push(diffRow);

      [
        ['gl_bridge_revenue', '  ℹ of which revenue: orders vs ledger revenue', glNi.bridge.revenue_difference],
        ['gl_bridge_cogs', '  ℹ of which COGS: order COGS vs ledger COGS', glNi.bridge.cogs_difference],
        ['gl_bridge_other', '  ℹ of which other costs: fees, fulfilment, marketing, OpEx vs ledger expenses', glNi.bridge.other_costs_difference]
      ].forEach(([id, label, total]) => {
        const r = { id, label, rowType: 'info', isPercentage: false, total };
        allPeriods.forEach(periodKey => { r[periodKey] = null; });  // bridge is period-total only
        rows.push(r);
      });
    }

    // Legacy QuickBooks-sourced section removed 2026-04-29 — internal GL is the source of truth.

    // CASH POSITION
    const cashDetails = plData.details.cashBalances || [];
    // DT-1995: a failed cash query returns no rows; show the row with the note instead of dropping the section.
    const cashError = plData.summary?.ending_cash_balance_error;
    if (cashDetails.length > 0 || cashError) {
      rows.push({ id: 'spacer_cash', label: '', rowType: 'spacer' });
      rows.push({ id: 'header_cash', label: 'CASH POSITION', rowType: 'header' });

      const endingCash = {
        id: 'ending_cash_balance',
        label: unavailable('Ending Cash Balance', cashError),
        rowType: 'cash',
        isPercentage: false,
        total: plData.summary?.ending_cash_balance ?? 0
      };

      allPeriods.forEach(periodKey => {
        const periodData = cashDetails.find(item => {
          const key = item.period;
          return key === periodKey;
        });
        endingCash[periodKey] = periodData ? (periodData.total_cash || 0) : null;
      });

      rows.push(endingCash);
    }

    return rows;
    // selectedChannel gates GL-only channel injection in filtered views (2026-07-06 leak fix)
  }, [plData, selectedChannel]);

  // Get unique periods from data
  const periods = useMemo(() => {
    if (!plData || !plData.details || !plData.details.revenue) return [];

    const periodsSet = new Set();
    Object.values(plData.details).forEach(detailArray => {
      if (Array.isArray(detailArray)) {
        detailArray.forEach(item => {
          const periodKey = item.period || item.group_key;
          if (periodKey) periodsSet.add(periodKey);
        });
      }
    });

    return Array.from(periodsSet).sort();
  }, [plData]);

  // Define columns dynamically based on periods
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'label',
        id: 'label',
        header: 'Line Item',
        size: 250,
        enableSorting: false,
        headerStyle: { backgroundColor: '#f5f5f5' },
      },
    ];

    // Add a column for each period
    periods.forEach(period => {
      cols.push({
        accessorKey: period,
        id: period,
        header: formatPeriodHeader(period, periodView),
        size: 120,
        align: 'right',
        headerStyle: { backgroundColor: '#e3f2fd' },
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.rowType === 'header' || row.original.rowType === 'spacer') return '';
          if (value === null || value === undefined) return '-';

          if (row.original.isPercentage) {
            return formatPercent(value);
          }
          return formatCurrency(value);
        },
      });
    });

    // Add total column
    cols.push({
      accessorKey: 'total',
      id: 'total',
      header: 'Total',
      size: 120,
      align: 'right',
      headerStyle: { backgroundColor: '#fff9c4' },
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.rowType === 'header' || row.original.rowType === 'spacer') return '';
        if (value === null || value === undefined) return '-';

        if (row.original.isPercentage) {
          return formatPercent(value);
        }
        return formatCurrency(value);
      },
    });

    return cols;
  }, [periods, periodView]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleExport = () => {
    if (!tableData || tableData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(
      tableData.map(row => {
        const exportRow = { 'Line Item': row.label };
        periods.forEach(period => {
          exportRow[formatPeriodHeader(period, periodView)] = row[period] || 0;
        });
        exportRow['Total'] = row.total || 0;
        return exportRow;
      })
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'P&L Statement');
    XLSX.writeFile(wb, `PL_Statement_${dateRange.start}_to_${dateRange.end}.xlsx`);
  };

  // Multi-sheet drilled monthly export — opens a small picker dialog so
  // the accountant can choose which month, then calls backend.
  const [exportingMonthly, setExportingMonthly] = React.useState(false);
  const [exportPickerOpen, setExportPickerOpen] = React.useState(false);
  const [exportPickerMonth, setExportPickerMonth] = React.useState(() => {
    // Default: the latest period in the visible range.
    if (!periods || periods.length === 0) {
      const now = new Date();
      return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
    }
    const last = periods[periods.length - 1];
    return last && /^\d{4}-\d{2}/.test(last) ? `${last.slice(0, 7)}-01` : null;
  });

  // Refresh default whenever periods change.
  React.useEffect(() => {
    if (periods && periods.length > 0) {
      const last = periods[periods.length - 1];
      if (last && /^\d{4}-\d{2}/.test(last)) {
        setExportPickerMonth(`${last.slice(0, 7)}-01`);
      }
    }
  }, [periods]);

  const handleExportMonthly = async () => {
    const month = exportPickerMonth;
    if (!month || !/^\d{4}-\d{2}-01$/.test(month)) {
      alert('Pick a valid month');
      return;
    }
    setExportingMonthly(true);
    setExportPickerOpen(false);
    try {
      const blob = await financialConfigService.exportMonthlyExcel({
        month,
        brand_id: selectedView !== 'all' && !isNaN(Number(selectedView)) ? Number(selectedView) : null,
        market: (typeof selectedGeography !== 'undefined' && selectedGeography && selectedGeography !== 'ALL') ? selectedGeography : null,
        channel: (typeof selectedChannel !== 'undefined' && selectedChannel && selectedChannel !== 'ALL') ? selectedChannel : null,
        basis: basis === 'accrual' ? 'accrual' : null,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pl_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Monthly export failed:', err);
      alert('Monthly export failed: ' + (err?.response?.data?.error || err.message));
    } finally {
      setExportingMonthly(false);
    }
  };

  // All-bank-transactions export — single sheet, every txn from every bank/card
  // source over an arbitrary date range (no per-account splitting like the
  // Google Sheet has). Defaults to the last whole month on the page.
  const [exportingBankTx, setExportingBankTx] = React.useState(false);
  const [bankTxPickerOpen, setBankTxPickerOpen] = React.useState(false);
  const [bankTxStart, setBankTxStart] = React.useState(() => {
    const now = new Date();
    return toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [bankTxEnd, setBankTxEnd] = React.useState(() => {
    const now = new Date();
    return toLocalISO(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });

  React.useEffect(() => {
    if (periods && periods.length > 0) {
      const last = periods[periods.length - 1];
      if (last && /^\d{4}-\d{2}/.test(last)) {
        const y = Number(last.slice(0, 4));
        const m = Number(last.slice(5, 7));
        // new Date(y, m, 0) is the last day of month m (m is 1-indexed here).
        setBankTxStart(toLocalISO(new Date(y, m - 1, 1)));
        setBankTxEnd(toLocalISO(new Date(y, m, 0)));
      }
    }
  }, [periods]);

  // Mirrors MAX_EXPORT_SPAN_DAYS in backend/finance/transactions/routes.js so the UI
  // never offers a range the API will reject with a 400.
  const BANK_TX_MAX_SPAN_DAYS = 400;
  const bankTxSpanDays = (bankTxStart && bankTxEnd)
    ? (Date.parse(`${bankTxEnd}T00:00:00Z`) - Date.parse(`${bankTxStart}T00:00:00Z`)) / 86400000
    : 0;
  const bankTxRangeInvalid = !bankTxStart || !bankTxEnd
    || bankTxEnd < bankTxStart
    || bankTxSpanDays > BANK_TX_MAX_SPAN_DAYS;

  const handleExportBankTransactions = async () => {
    if (bankTxRangeInvalid) {
      alert(`Pick a start and end date (end must not be before start, and the range must not exceed ${BANK_TX_MAX_SPAN_DAYS} days)`);
      return;
    }
    const startDate = bankTxStart;
    const endDate = bankTxEnd;
    setExportingBankTx(true);
    setBankTxPickerOpen(false);
    try {
      const blob = await financialConfigService.exportMonthlyBankTransactions({ startDate, endDate });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank-transactions-${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Bank transactions export failed:', err);
      alert('Bank transactions export failed: ' + (err?.response?.data?.error || err.message));
    } finally {
      setExportingBankTx(false);
    }
  };

  if (loading && !plData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptLong sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              P&L Statement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {basis === 'accrual'
                ? 'Income Statement (accrual) - OpEx re-dated to invoice month for invoice-covered vendors on the company-level view; brand-filtered views stay payment-dated'
                : 'Income Statement - Revenue, costs, and profitability by period'}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Single-sheet export is client-side from the on-screen table, so it
              always matches the displayed basis. The Drilled Monthly workbook
              runs on the same basis flag as the page (2026-07-10) and, on the
              accrual view, includes an "Accrual Bridge" tab showing both dates:
              invoice-dated bills counted vs payment-dated JEs re-dated out. */}
          <Tooltip title="Export current view to Excel (single sheet, summary)">
            <span>
              <IconButton onClick={handleExport} size="small" disabled={!plData}>
                <FileDownload />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={basis === 'accrual'
            ? 'Pick a month → multi-sheet workbook (Summary + drilled tabs per line), on the same accrual basis as this page. Includes an Accrual Bridge tab listing both dates: invoice-dated bills counted vs payment-dated entries re-dated out.'
            : 'Pick a month → multi-sheet workbook (Summary + drilled tabs per line)'}>
            <span>
              <Button
                onClick={() => setExportPickerOpen(true)}
                size="small"
                variant="outlined"
                disabled={!plData || exportingMonthly}
                startIcon={<FileDownload />}
              >
                {exportingMonthly ? 'Exporting…' : 'Drilled Monthly'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Pick a date range → single sheet of every queued bank transaction across amex / revolut / wise / paypal, including booked GL account. Not a bank statement — excludes PayPal order receipts.">
            <Button
              onClick={() => setBankTxPickerOpen(true)}
              size="small"
              variant="outlined"
              disabled={exportingBankTx}
              startIcon={<FileDownload />}
            >
              {exportingBankTx ? 'Exporting…' : 'Bank Transactions'}
            </Button>
          </Tooltip>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadPLData} size="small" disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* DT-1995: a brand whose orders check failed is missing from every line of this P&L. */}
      {plData?.summary?.brand_databases_error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Incomplete P&L</strong> — UNAVAILABLE: {plData.summary.brand_databases_error}.
        </Alert>
      )}
      {/* P&L (Accrual) v1: basis banner — states what is (and isn't) re-dated, with
          the cash→accrual adjustment totals for the selected range. */}
      {/* Fallback: backend didn't return basis_meta (not deployed / errored) — say so
          rather than silently rendering numbers indistinguishable from the cash page. */}
      {basis === 'accrual' && plData && !plData.basis_meta && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Accrual re-dating unavailable for this view</strong> — the API did not
          return accrual metadata, so the figures below are payment-dated (cash basis),
          identical to the standard P&L.
        </Alert>
      )}
      {basis === 'accrual' && plData?.basis_meta && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Accrual basis (v1):</strong>{' '}
          {plData.basis_meta.accrual_applied ? (
            <span>
              OpEx for invoice-covered vendors is re-dated to invoice month
              {Array.isArray(plData.basis_meta.accrual_adjustments) && plData.basis_meta.accrual_adjustments.length > 0 && (() => {
                const rem = plData.basis_meta.accrual_adjustments.reduce((s, r) => s + (Number(r.removed_bank_jes_usd) || 0), 0);
                const add = plData.basis_meta.accrual_adjustments.reduce((s, r) => s + (Number(r.added_invoices_usd) || 0), 0);
                // Coverage disclosure: what share of the displayed OpEx is actually
                // invoice-dated. Without this, the "(Accrual)" title over-claims —
                // the majority of OpEx is still payment-dated until Phase 2.
                const opexTotal = Number(plData.summary?.opex_total) || 0;
                const pct = opexTotal > 0 ? Math.min(100, Math.round((add / opexTotal) * 100)) : null;
                return (
                  <span>
                    {' '}— this range: {formatCurrency(rem)} payment-timed entries replaced by {formatCurrency(add)} invoice-dated
                    {pct !== null && <strong>{' '}(~{pct}% of OpEx shown is invoice-dated; the rest is still payment-dated)</strong>}
                  </span>
                );
              })()}
              {/* DT-1995: the re-dating applied, but its adjustment totals failed to load: say so instead of
                  showing no totals as if nothing was re-dated. */}
              {plData.summary?.accrual_adjustments_error && (
                <span>{' '}— the adjustment totals for this range are UNAVAILABLE ({plData.summary.accrual_adjustments_error})</span>
              )}
              . All other expenses remain payment-dated until the AP ledger posting (Phase 2) lands.
            </span>
          ) : (
            <span>
              re-dating applies on the company-level view only — this brand-filtered view shows payment-dated (cash) OpEx.
            </span>
          )}
        </Alert>
      )}

      {/* Stage 1.4: GL integrity banner — surfaces issues that inflate OpEx and bias EBITDA */}
      {glIntegrity && (glIntegrity.totals?.duplicate > 0 || glIntegrity.totals?.pending > 0) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => { window.location.href = '/finance/journal-reconciliation'; }}
            >
              Review →
            </Button>
          }
        >
          <strong>GL integrity:</strong>{' '}
          {glIntegrity.totals.duplicate > 0 && (
            <span>
              {glIntegrity.totals.duplicate.toLocaleString()} duplicate JEs detected
              {' '}— OpEx may be overstated.
              {' '}
            </span>
          )}
          {glIntegrity.totals.pending > 0 && (
            <span>
              {glIntegrity.totals.pending.toLocaleString()} pending bank-queue rows
              {' '}— OpEx may be understated until classified.
            </span>
          )}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedView}
                  onChange={(e) => setSelectedView(e.target.value)}
                  label="Brand"
                >
                  <MenuItem value="all">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="All Brands" size="small" color="primary" />
                    </Box>
                  </MenuItem>
                  {brands && brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Period View</InputLabel>
                <Select
                  value={periodView}
                  onChange={(e) => setPeriodView(e.target.value)}
                  label="Period View"
                >
                  <MenuItem value="annual">Annual</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={datePreset}
                  onChange={(e) => handleDatePresetChange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="last_6_months">Last 6 Months</MenuItem>
                  <MenuItem value="last_year">Last Year</MenuItem>
                  <MenuItem value="this_year">This Year</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Geography</InputLabel>
                <Select
                  value={selectedGeography}
                  onChange={(e) => setSelectedGeography(e.target.value)}
                  label="Geography"
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="US">US</MenuItem>
                  <MenuItem value="CA">CA</MenuItem>
                  <MenuItem value="AU">AU</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="EU">EU</MenuItem>
                  <MenuItem value="INT">INT (Others)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Channel</InputLabel>
                <Select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  label="Channel"
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="D2C">D2C</MenuItem>
                  <MenuItem value="Amazon">Amazon</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, start: e.target.value });
                    setDatePreset('custom');
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    width: '100%',
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, end: e.target.value });
                    setDatePreset('custom');
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    width: '100%',
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* P&L Table */}
      {plData && tableData.length > 0 && (
        <Paper elevation={2}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6">
              Period: {plData.period.startDate} to {plData.period.endDate}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All amounts in USD
            </Typography>
          </Box>

          {/* Table Container */}
          <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
            <table
              style={{
                width: table.getTotalSize(),
                minWidth: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                tableLayout: 'fixed',
              }}
            >
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          padding: '8px 12px',
                          textAlign: header.column.columnDef.align || 'left',
                          backgroundColor: header.column.columnDef.headerStyle?.backgroundColor || '#f5f5f5',
                          borderBottom: '2px solid #ddd',
                          borderRight: '1px solid #f0f0f0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          width: header.getSize(),
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: header.column.columnDef.align === 'right' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUpward sx={{ fontSize: 16 }} />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDownward sx={{ fontSize: 16 }} />
                              ) : (
                                <UnfoldMore sx={{ fontSize: 16, opacity: 0.3 }} />
                              )}
                            </Box>
                          )}
                        </Box>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const rowType = row.original.rowType;
                  const isHeader = rowType === 'header';
                  const isSubtotal = rowType === 'subtotal';
                  const isTotal = rowType === 'total';
                  const isFinal = rowType === 'final';
                  const isSpacer = rowType === 'spacer';
                  const isCash = rowType === 'cash';

                  let rowStyle = {
                    borderBottom: '1px solid #eee',
                  };

                  if (isSpacer) {
                    rowStyle = {
                      ...rowStyle,
                      height: '12px',
                      borderBottom: 'none',
                    };
                  } else if (isHeader) {
                    rowStyle = {
                      ...rowStyle,
                      backgroundColor: '#F4A460',
                      fontWeight: 700,
                    };
                  } else if (isFinal) {
                    rowStyle = {
                      ...rowStyle,
                      backgroundColor: '#90EE90',
                      fontWeight: 700,
                    };
                  } else if (isCash) {
                    rowStyle = {
                      ...rowStyle,
                      backgroundColor: '#E0E7FF',
                      fontWeight: 600,
                    };
                  } else if (isTotal) {
                    rowStyle = {
                      ...rowStyle,
                      backgroundColor: '#ADD8E6',
                      fontWeight: 700,
                    };
                  } else if (isSubtotal) {
                    rowStyle = {
                      ...rowStyle,
                      backgroundColor: '#FFFACD',
                      fontWeight: 600,
                    };
                  } else if (rowType === 'info') {
                    // S1.2c v3: info-only rows (e.g. GL 7100/7200) — italic + muted
                    rowStyle = {
                      ...rowStyle,
                      fontStyle: 'italic',
                      color: '#666',
                    };
                  }

                  const rowDrillable = isRowDrillable(row.original);

                  return (
                    <tr key={row.id} style={rowStyle}>
                      {row.getVisibleCells().map(cell => {
                        const columnDef = cell.column.columnDef;
                        const value = cell.getValue();
                        const numValue = Number(value);

                        // A cell is interactive if its row is Phase-1 drillable.
                        // The label/Total columns drill the full date range; period columns drill that period.
                        const interactive = rowDrillable;

                        let cellStyle = {
                          padding: '6px 12px',
                          textAlign: columnDef.align || 'left',
                          borderRight: '1px solid #f0f0f0',
                          fontWeight: isHeader || isTotal || isFinal ? 700 : (isSubtotal || isCash) ? 600 : 400,
                          width: cell.column.getSize(),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: interactive ? 'pointer' : 'default',
                          transition: 'background-color 0.1s',
                        };

                        // Red color for negative values (except headers)
                        if (!isHeader && !isNaN(numValue) && numValue < 0) {
                          cellStyle.color = '#c62828';
                        }

                        // Info rows carry one-off explanatory sentences, not tabular
                        // labels — wrap them inside the label column instead of
                        // ellipsis-truncating (tooltip stays as a fallback).
                        if (row.original.rowType === 'info' && cell.column.id === 'label') {
                          cellStyle.whiteSpace = 'normal';
                        }

                        const handleClick = interactive ? () => {
                          const colId = cell.column.id;
                          // 'label' (row label cell) and 'total' both drill the full range.
                          const period = (colId === 'label' || colId === 'total') ? null : colId;
                          setDrillTarget({
                            row_id: row.original.id,
                            period,
                            drill_path: [],
                          });
                        } : undefined;

                        return (
                          <td
                            key={cell.id}
                            style={cellStyle}
                            onClick={handleClick}
                            onMouseEnter={interactive ? (e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(33, 150, 243, 0.10)';
                            } : undefined}
                            onMouseLeave={interactive ? (e) => {
                              e.currentTarget.style.backgroundColor = '';
                            } : undefined}
                            title={interactive
                              ? 'Click to drill down'
                              // Info rows carry explanatory sentences that the 250px
                              // ellipsised label column cuts off — hover shows the full text.
                              : (row.original.rowType === 'info' && cell.column.id === 'label'
                                  ? row.original.label.trim()
                                  : undefined)}
                          >
                            {isHeader && cell.column.id === 'label' ? (
                              <span style={{ fontWeight: 700 }}>{row.original.label}</span>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Data Sources:</strong> Revenue & COGS from Shopify/Amazon/wholesale orders, Marketing from ads platforms, Operating expenses from the internal ledger (bank/Amex-fed journal entries), Cash from Revolut/PayPal/Wise/Shopify
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Loading Overlay */}
      {loading && plData && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Drill-down Drawer */}
      <DrillDownDrawer
        open={drillTarget !== null}
        target={drillTarget}
        filters={{
          brand_id: selectedView === 'all' ? null : parseInt(selectedView),
          start_date: dateRange.start,
          end_date: dateRange.end,
          market: selectedGeography === 'ALL' ? null : selectedGeography,
          channel: selectedChannel === 'ALL' ? null : selectedChannel,
          basis: basis === 'accrual' ? 'accrual' : null,
          // Grain flows to the cash drill so an annual/monthly/daily cell drills the
          // SAME bucket the parent aggregated (year cell => whole-year ending balance,
          // not January). Cash handler defaults to 'month' if absent (back-compat).
          group_by: periodView === 'annual' ? 'year' : periodView === 'daily' ? 'day' : 'month',
        }}
        plPayload={plData}
        onClose={() => setDrillTarget(null)}
        onNavigate={(next) => setDrillTarget(next)}
      />

      {/* Monthly Excel export — month picker */}
      <Dialog open={exportPickerOpen} onClose={() => setExportPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export drilled monthly P&amp;L</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Builds a multi-sheet Excel workbook for one month: Summary + per-line drilled tabs +
            marketing channels + OpEx categories.
          </Typography>
          <TextField
            label="Month"
            type="month"
            fullWidth
            size="small"
            value={exportPickerMonth ? exportPickerMonth.slice(0, 7) : ''}
            onChange={(e) => {
              const v = e.target.value;
              setExportPickerMonth(v ? `${v}-01` : null);
            }}
            InputLabelProps={{ shrink: true }}
            helperText="Picks the calendar month to export. Honors the current Brand / Geography / Channel filters."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportPickerOpen(false)} disabled={exportingMonthly}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportMonthly}
            disabled={exportingMonthly || !exportPickerMonth}
          >
            {exportingMonthly ? 'Exporting…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank-transactions Excel export — date-range picker */}
      <Dialog open={bankTxPickerOpen} onClose={() => setBankTxPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export all bank transactions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Single-sheet workbook of every bank/card transaction in the chosen date
            range that entered the reconciliation queue (amex, revolut, wise, paypal),
            with its booked GL account, journal entry id and status.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Not a bank statement: PayPal customer order receipts are excluded (they are
            booked via order revenue), and there is no running balance or fee column.
            For an external accountant, send the bank's own statement instead.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="From"
              type="date"
              fullWidth
              size="small"
              value={bankTxStart || ''}
              onChange={(e) => setBankTxStart(e.target.value || '')}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              fullWidth
              size="small"
              value={bankTxEnd || ''}
              onChange={(e) => setBankTxEnd(e.target.value || '')}
              InputLabelProps={{ shrink: true }}
              error={Boolean(bankTxStart && bankTxEnd && bankTxEnd < bankTxStart)}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {bankTxStart && bankTxEnd && bankTxEnd < bankTxStart
              ? 'End date must not be before the start date.'
              : bankTxSpanDays > BANK_TX_MAX_SPAN_DAYS
                ? `Range must not exceed ${BANK_TX_MAX_SPAN_DAYS} days (currently ${Math.round(bankTxSpanDays)}).`
                : 'Both dates are inclusive. Cross-bank, single sheet.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankTxPickerOpen(false)} disabled={exportingBankTx}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportBankTransactions}
            disabled={exportingBankTx || bankTxRangeInvalid}
          >
            {exportingBankTx ? 'Exporting…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Helper function to format period headers
function formatPeriodHeader(period, periodView) {
  if (!period) return 'Unknown';

  // Period is in ISO format from ClickHouse (e.g., "2024-01-01")
  const date = new Date(period);

  if (periodView === 'annual') {
    return date.getFullYear().toString();
  } else if (periodView === 'monthly') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  } else if (periodView === 'daily') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return period;
}
